import {
  CHARACTER_STATUSES,
  type CharacterDetailResponse,
  type CharacterStatus,
  type FactEntry,
} from '@brimstone/shared';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { EmptyState, PageLayout, Panel, Tag, Toggle } from '../../components/ui';
import { ChronicleFeed } from '../../components/ChronicleFeed';
import { FishingPanel } from '../../components/FishingPanel';
import { PhotosPanel } from '../../components/PhotosPanel';
import { GroupedWithPanel } from '../people/PeoplePages';
import { WhereWasIPanel } from '../../components/WhereWasIPanel';
import { useCharacterChronicle } from '../../lib/chronicle';
import { raceAndClass, useCharacter, useUpdateCharacter } from '../../lib/roster';
import { formatAge, formatClock, formatPlayed } from '../../lib/time';
import { ShowOnMap, characterSpot } from '../../components/ShowOnMap';

/** Classic's talent tab order (left to right), for naming the tab with the most points. */
const TALENT_TABS: Record<string, [string, string, string]> = {
  DRUID: ['Balance', 'Feral Combat', 'Restoration'],
  HUNTER: ['Beast Mastery', 'Marksmanship', 'Survival'],
  MAGE: ['Arcane', 'Fire', 'Frost'],
  PALADIN: ['Holy', 'Protection', 'Retribution'],
  PRIEST: ['Discipline', 'Holy', 'Shadow'],
  ROGUE: ['Assassination', 'Combat', 'Subtlety'],
  SHAMAN: ['Elemental', 'Enhancement', 'Restoration'],
  WARLOCK: ['Affliction', 'Demonology', 'Destruction'],
  WARRIOR: ['Arms', 'Fury', 'Protection'],
};

/** "PLAN" for seeded values, else how old the observation is. */
function age(entry: FactEntry<unknown> | undefined): string | undefined {
  if (!entry) return undefined;
  return entry.source === 'seed' ? 'PLAN' : `as of ${formatAge(entry.observedAt)}`;
}

function Rows({ children }: { children: ReactNode }) {
  return (
    <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 px-4 py-3 text-compact">
      {children}
    </dl>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}

function Identity({ data }: { data: CharacterDetailResponse }) {
  const { character: c, facts } = data;
  return (
    <Panel
      title={c.name}
      meta={
        c.level !== null ? `${c.status.toUpperCase()} · LVL ${c.level}` : c.status.toUpperCase()
      }
    >
      <Rows>
        <Row label="Full name">
          {c.fullName ?? <span className="text-muted">not seen yet</span>}
        </Row>
        <Row label="Race and class">{raceAndClass(c)}</Row>
        <Row label="Level">
          <span className="font-mono">{c.level ?? '—'}</span>
        </Row>
        <Row label="Ruleset">{c.realm ?? <span className="text-muted">—</span>}</Row>
        <Row label="Zone">
          {facts['location.zone']?.value ?? <span className="text-muted">no data</span>}
          {facts['location.subzone']?.value ? ` · ${facts['location.subzone'].value}` : ''}{' '}
          <ShowOnMap link={characterSpot(c.id, facts['location.zone']?.value)} character={c.id} />
        </Row>
        <Row label="Last logout">
          {c.lastSeenAt ? (
            <span className="font-mono">
              {formatClock(c.lastSeenAt)} · {formatAge(c.lastSeenAt)}
            </span>
          ) : (
            <span className="text-muted">planned: not played yet</span>
          )}
        </Row>
      </Rows>
    </Panel>
  );
}

function Professions({ data }: { data: CharacterDetailResponse }) {
  const { facts } = data;
  const profs = facts.professions;
  const plan = facts['plan.professions']?.value ?? [];
  const learned = facts['professions.specializations']?.value ?? [];
  const planned = (name: string) => plan.find((p) => p.name === name)?.specialization;
  return (
    <Panel title="Professions" meta={age(profs)}>
      {!profs || profs.value.length === 0 ? (
        <EmptyState>No professions recorded yet.</EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col px-4 py-2">
          {profs.value.map((p) => (
            <li
              key={p.name}
              className="flex flex-wrap items-baseline justify-between gap-x-3 border-b border-line-soft py-2 last:border-b-0"
            >
              <span className="flex items-baseline gap-2">
                <span className={p.primary ? 'text-name' : 'text-compact text-muted'}>
                  {p.name}
                </span>
                {planned(p.name) && (
                  <span className="text-label text-muted">plan: {planned(p.name)}</span>
                )}
              </span>
              <span className="font-mono text-secondary">
                {profs.source === 'seed' ? (
                  <span className="text-muted">planned</span>
                ) : (
                  `${p.rank}${p.max ? ` / ${p.max}` : ''}`
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {learned.length > 0 && (
        <p className="m-0 border-t border-line px-4 py-3 text-compact">
          <span className="text-muted">Specialization: </span>
          {learned.join(', ')}
        </p>
      )}
    </Panel>
  );
}

function SpecsAndPets({ data }: { data: CharacterDetailResponse }) {
  const { facts, character } = data;
  const specs = facts['plan.specs']?.value ?? [];
  const mainTab = facts['talents.specs']?.value.mainTab ?? null;
  const tabs = character.classToken ? TALENT_TABS[character.classToken] : undefined;
  const pets = facts.pets?.value;
  const plannedPets = facts['plan.pets']?.value ?? [];
  return (
    <Panel title="Specs and pets">
      <Rows>
        <Row label="Planned specs">
          {specs.length ? specs.join(' · ') : <span className="text-muted">none planned</span>}
        </Row>
        <Row label="Talents">
          {mainTab && tabs ? (
            `Most points in ${tabs[mainTab - 1]}`
          ) : (
            <span className="text-muted">no data</span>
          )}
          {facts['talents.unspent']?.value ? (
            <span className="ml-2 font-mono text-accent">
              {facts['talents.unspent'].value} UNSPENT
            </span>
          ) : null}
        </Row>
        {(pets?.current || plannedPets.length > 0) && (
          <Row label="Pets">
            <span className="flex flex-col gap-1">
              {pets?.current && (
                <span>
                  {pets.current.name}{' '}
                  <span className="text-muted">({pets.current.family}, out)</span>
                </span>
              )}
              {(pets?.stable ?? []).map((p) => (
                <span key={`s-${p.name}`}>
                  {p.name} <span className="text-muted">({p.family}, stabled)</span>
                </span>
              ))}
              {plannedPets
                .filter(
                  (p) =>
                    p.name !== pets?.current?.name && !pets?.stable.some((s) => s.name === p.name),
                )
                .map((p) => (
                  <span key={`p-${p.name}`}>
                    {p.name}{' '}
                    <span className="text-muted">
                      ({p.family}, planned{p.use ? `: ${p.use}` : ''})
                    </span>
                  </span>
                ))}
            </span>
          </Row>
        )}
      </Rows>
    </Panel>
  );
}

function Plan({ data }: { data: CharacterDetailResponse }) {
  const notes = data.facts['plan.notes']?.value ?? [];
  const secondary = data.facts['plan.secondary']?.value ?? [];
  return (
    <Panel title="Plan" meta="ROSTER DOC">
      <Rows>
        <Row label="Secondary">{secondary.length ? secondary.join(', ') : '—'}</Row>
        <Row label="Notes">
          {notes.length ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : (
            '—'
          )}
        </Row>
        <Row label="Wanted recipes">
          {data.wantedRecipes.length ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {data.wantedRecipes.map((w) => (
                <li key={w.id}>
                  {w.item}{' '}
                  <span className="text-muted">
                    · {w.profession}
                    {w.forNames.length ? ` · for ${w.forNames.join(', ')}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            '—'
          )}
        </Row>
      </Rows>
    </Panel>
  );
}

function Flags({ data }: { data: CharacterDetailResponse }) {
  const update = useUpdateCharacter();
  const c = data.character;
  const set = (patch: Record<string, boolean | CharacterStatus>) =>
    update.mutate({ id: c.id, ...patch });
  return (
    <Panel title="Flags" level="side" meta={c.inPool ? undefined : 'NOT IN POOL'}>
      <div className="flex flex-col px-4 py-2">
        <Toggle
          label="In the roster"
          hint="Shown in the app"
          checked={c.inRoster}
          disabled={update.isPending}
          onChange={(v) => set({ inRoster: v })}
        />
        <Toggle
          label="Duo only"
          hint="Kept out of Tonight and the fate roll"
          checked={c.duoOnly}
          disabled={update.isPending}
          onChange={(v) => set({ duoOnly: v })}
        />
        <Toggle
          label="In tonight’s pool"
          hint="Can be ranked and rolled"
          checked={c.inTonightPool}
          disabled={update.isPending}
          onChange={(v) => set({ inTonightPool: v })}
        />
        <label className="flex min-h-11 items-center justify-between gap-3">
          <span className="text-compact">Status</span>
          <select
            value={c.status}
            disabled={update.isPending}
            onChange={(e) => set({ status: e.target.value as CharacterStatus })}
            className="min-h-11 border border-line bg-bg px-3 font-mono text-secondary uppercase"
          >
            {CHARACTER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      {update.isError && (
        <p className="m-0 px-4 pb-3 text-secondary text-danger">{update.error.message}</p>
      )}
    </Panel>
  );
}

/** The character's slice of the Chronicle (SPEC §6.6). */
function ChronicleSlice({ characterId, name }: { characterId: string; name: string }) {
  const q = useCharacterChronicle(characterId);
  const entries = q.data?.entries;
  return (
    <Panel title="Chronicle">
      {!entries ? (
        <EmptyState>{q.isError ? 'Couldn’t load it.' : 'Loading…'}</EmptyState>
      ) : entries.length === 0 ? (
        <EmptyState>Nothing yet — entries appear after {name}’s next logout.</EmptyState>
      ) : (
        <>
          <ChronicleFeed entries={entries} />
          <p className="m-0 border-t border-line px-4 py-3 text-compact">
            <Link to={`/chronicle?character=${characterId}`}>All of {name}’s Chronicle</Link>
          </p>
        </>
      )}
    </Panel>
  );
}

/** `/played` from Alts Forever and this reset week's sessions (SPEC §6.8). */
function Playtime({ data }: { data: CharacterDetailResponse }) {
  const { played } = data;
  const time = (s: number | null) =>
    s === null ? (
      <span className="text-muted">no data</span>
    ) : (
      <span className="font-mono">{formatPlayed(s)}</span>
    );
  return (
    <Panel
      title="Playtime"
      level="side"
      meta={played.totalAsOf ? `as of ${formatAge(played.totalAsOf)}` : undefined}
    >
      <Rows>
        <Row label="/played">{time(played.totalSeconds)}</Row>
        <Row label="This week">{time(played.thisWeekSeconds)}</Row>
      </Rows>
    </Panel>
  );
}

/** /book/:id: one character's page (M4). */
export function BookPage() {
  const { id = '' } = useParams();
  const q = useCharacter(id);
  if (!q.data) {
    return (
      <PageLayout
        main={
          <Panel title="Book">
            <EmptyState>
              {q.isError ? (
                <>
                  No such character. <Link to="/book">Back to the Book</Link>
                </>
              ) : (
                'Loading…'
              )}
            </EmptyState>
          </Panel>
        }
      />
    );
  }
  const data = q.data;
  return (
    <PageLayout
      main={
        <>
          <Identity data={data} />
          <Professions data={data} />
          <SpecsAndPets data={data} />
          <Plan data={data} />
          <ChronicleSlice characterId={data.character.id} name={data.character.name} />
          <PhotosPanel
            filters={{ characterIds: [data.character.id], week: null }}
            limit={8}
            empty={`No photos of ${data.character.name} yet — Memento’s screenshots appear after the watcher uploads them.`}
          />
        </>
      }
      side={
        <>
          <Flags data={data} />
          <WhereWasIPanel characterId={data.character.id} />
          <GroupedWithPanel characterId={data.character.id} name={data.character.name} />
          <FishingPanel characterId={data.character.id} name={data.character.name} />
          <Panel title="Legacy contribution" level="side">
            <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 px-4 py-3 text-compact">
              <dt className="text-muted">Reached</dt>
              <dd className="m-0">
                {data.legacy.reached.length ? (
                  data.legacy.reached.join(', ')
                ) : (
                  <span className="text-muted">no milestone yet</span>
                )}
              </dd>
              <dt className="text-muted">Next</dt>
              <dd className="m-0">
                {data.legacy.next ? (
                  <>
                    {data.legacy.next.label}{' '}
                    <span className="font-mono text-muted">
                      {data.legacy.next.distance}{' '}
                      {data.legacy.next.unit === 'level' ? 'lvl' : 'skill'}
                    </span>
                  </>
                ) : (
                  <span className="text-muted">no data</span>
                )}
              </dd>
            </dl>
          </Panel>
          {data.character.duoOnly && (
            <p className="m-0 flex items-center gap-2 text-secondary text-muted">
              <Tag>DUO</Tag> Played only in the duo.
            </p>
          )}
          <Playtime data={data} />
        </>
      }
    />
  );
}
